import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from './Checkbox';
import { filterTriggerHover } from './filterTriggerStyles';
import { cn } from '@/utils/cn';

/**
 * TagsMultiSelect Component
 *
 * Multi-select dropdown for tags with search, checkboxes, and color-coded badges.
 */

interface TagOption {
  name: string;
  color?: string;
  count?: number;
}

interface TagsMultiSelectProps {
  value: string[];
  onChange: (tags: string[]) => void;
  availableTags: TagOption[];
  placeholder?: string;
  className?: string;
  size?: 'sm' | 'md';
}

export const TagsMultiSelect = ({
  value,
  onChange,
  availableTags,
  placeholder = 'Select tags',
  className = '',
  size = 'md',
}: TagsMultiSelectProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [openUp, setOpenUp] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Auto-detect direction
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setOpenUp(window.innerHeight - rect.bottom < 320);
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch(''); // Reset search on close
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  /**
   * Filter tags based on search query
   * Only include tags with valid names
   */
  const filteredTags = availableTags.filter((tag) =>
    tag?.name?.toLowerCase()?.includes(search.toLowerCase()) ?? false
  );

  /**
   * Toggle tag selection
   */
  const toggleTag = (tagName: string) => {
    if (value.includes(tagName)) {
      onChange(value.filter((t) => t !== tagName));
    } else {
      onChange([...value, tagName]);
    }
  };

  /**
   * Get badge color for a tag
   * Default to primary color if no color specified
   */
  const getBadgeColor = (tag: TagOption): { bg: string; text: string } => {
    if (tag.color) {
      // Assume color is hex format, create light background variant
      return {
        bg: `${tag.color}20`, // 20 is ~12% opacity in hex
        text: tag.color,
      };
    }
    // Default primary colors (use CSS variables with fallback)
    return {
      bg: 'var(--bs-soft-mint, #D5EEEB)',
      text: 'var(--color-secondary-500, #074F47)',
    };
  };

  /**
   * Render selected tags as badges
   */
  const renderSelectedBadges = () => {
    if (value.length === 0) {
      return <span className="text-gray-400">{placeholder}</span>;
    }

    // Show first 2 tags + count indicator if more
    const displayTags = value.slice(0, 2);
    const remaining = value.length - 2;

    return (
      <div className="flex flex-wrap items-center gap-1">
        {displayTags.map((tagName) => {
          const tag = availableTags.find((t) => t.name === tagName);
          const colors = tag ? getBadgeColor(tag) : { bg: 'var(--bs-soft-mint, #D5EEEB)', text: 'var(--color-secondary-500, #074F47)' };

          return (
            <span
              key={tagName}
              className="px-2 py-0.5 text-xs rounded-md font-medium"
              style={{
                backgroundColor: colors.bg,
                color: colors.text,
              }}
            >
              {tagName}
            </span>
          );
        })}
        {remaining > 0 && (
          <span className="text-xs text-gray-500 font-medium">+{remaining}</span>
        )}
      </div>
    );
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      {/* Trigger Button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          size === 'sm'
            ? 'w-full px-3 h-[34px] text-left text-xs lg:h-auto lg:px-4 lg:py-3 lg:text-sm'
            : 'w-full px-4 py-3 text-left text-sm',
          'border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-gray-400 transition-colors duration-200',
          filterTriggerHover
        )}
      >
        {renderSelectedBadges()}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className={`absolute z-50 w-full max-w-md bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col ${openUp ? 'bottom-full mb-1' : 'mt-1'}`}>
          {/* Search Input */}
          <div className="p-3 border-b border-gray-100 sticky top-0 bg-white">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tags..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-gray-400"
              autoFocus
            />
          </div>

          {/* Tags List */}
          <div className="overflow-y-auto flex-1">
            {filteredTags.length > 0 ? (
              <div className="p-2">
                {filteredTags.map((tag) => {
                  const isSelected = value.includes(tag.name);
                  const colors = getBadgeColor(tag);

                  return (
                    <label
                      key={tag.name}
                      className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded-md cursor-pointer transition-colors"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleTag(tag.name)}
                        inline
                      />
                      <span className="flex items-center gap-2 flex-1">
                        <span
                          className="px-2 py-0.5 text-xs rounded-md font-medium"
                          style={{
                            backgroundColor: colors.bg,
                            color: colors.text,
                          }}
                        >
                          {tag.name}
                        </span>
                        {tag.count !== undefined && (
                          <span className="text-xs text-gray-400">
                            ({tag.count})
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            ) : (
              <div className="p-4 text-center">
                <p className="text-sm text-gray-500">
                  {search ? 'No tags found' : 'No tags available'}
                </p>
              </div>
            )}
          </div>

          {/* Footer with selected count */}
          {value.length > 0 && (
            <div className="p-2 border-t border-gray-100 bg-gray-50">
              <button
                type="button"
                onClick={() => onChange([])}
                className="w-full px-3 py-2 text-xs text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
              >
                {t('clear_all')} ({value.length} {t('selected', { count: value.length })})
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
